import type { BrachycephalySeverity, PlagiocephalySeverity } from './options.js';

/**
 * Calculs du bilan pédiatrique. La V1 les recopiait quatre fois dans des gestionnaires
 * `onChange` ; ils vivent ici une seule fois, lus par l'écran et vérifiables par les tests.
 */

function positive(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value) && value > 0;
}

function twoDecimals(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Indice d'asymétrie de la voûte crânienne :
 * `(diagonale longue − diagonale courte) / diagonale courte × 100`.
 */
export function cranialVaultAsymmetryIndex(
  diagonalRightMm: number | undefined,
  diagonalLeftMm: number | undefined,
): number | null {
  if (!positive(diagonalRightMm) || !positive(diagonalLeftMm)) return null;
  const longer = Math.max(diagonalRightMm, diagonalLeftMm);
  const shorter = Math.min(diagonalRightMm, diagonalLeftMm);
  return twoDecimals(((longer - shorter) / shorter) * 100);
}

/** Seuils de la V1 : ≥ 11 très sévère, ≥ 8,75 sévère, ≥ 6,25 modérée, ≥ 3,5 légère. */
export function plagiocephalySeverityOf(cvai: number): PlagiocephalySeverity {
  if (cvai >= 11) return 'very_severe';
  if (cvai >= 8.75) return 'severe';
  if (cvai >= 6.25) return 'moderate';
  if (cvai >= 3.5) return 'mild';
  return 'normal';
}

/** Indice céphalique : `largeur latérale / longueur antéro-postérieure × 100`. */
export function cephalicIndex(
  widthMm: number | undefined,
  lengthMm: number | undefined,
): number | null {
  if (!positive(widthMm) || !positive(lengthMm)) return null;
  return twoDecimals((widthMm / lengthMm) * 100);
}

/** Seuils de la V1 : ≥ 90 sévère, ≥ 86 modérée, ≥ 81 légère. */
export function brachycephalySeverityOf(index: number): BrachycephalySeverity {
  if (index >= 90) return 'severe';
  if (index >= 86) return 'moderate';
  if (index >= 81) return 'mild';
  return 'normal';
}

/** Âge à partir duquel la section disparaît de la navigation, en mois révolus. */
export const PEDIATRIC_AGE_LIMIT_MONTHS = 12;

/**
 * Mois révolus entre une date de naissance et une date de consultation, toutes deux
 * `YYYY-MM-DD`. `null` si l'une est illisible ou si la consultation précède la naissance.
 */
export function ageInMonthsAt(birthDate: string, onDate: string): number | null {
  const birth = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate);
  const at = /^(\d{4})-(\d{2})-(\d{2})$/.exec(onDate);
  if (!birth || !at) return null;
  const [by, bm, bd] = [Number(birth[1]), Number(birth[2]), Number(birth[3])];
  const [ay, am, ad] = [Number(at[1]), Number(at[2]), Number(at[3])];
  let months = (ay - by) * 12 + (am - bm);
  if (ad < bd) months -= 1;
  return months < 0 ? null : months;
}

/** Vrai si le patient a moins de douze mois à la date de la consultation. */
export function isInfantAt(birthDate: string, onDate: string): boolean {
  const months = ageInMonthsAt(birthDate, onDate);
  return months !== null && months < PEDIATRIC_AGE_LIMIT_MONTHS;
}

/**
 * Premier anniversaire, `YYYY-MM-DD` : le premier jour où `isInfantAt` devient faux. Un enfant né
 * un 29 février le passe le 1er mars d'une année commune. `null` si la date est illisible.
 */
export function firstBirthdayOf(birthDate: string): string | null {
  const birth = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate);
  if (!birth) return null;
  const [year, month, day] = [Number(birth[1]), Number(birth[2]), Number(birth[3])];
  return new Date(Date.UTC(year + 1, month - 1, day)).toISOString().slice(0, 10);
}
